/**
 * Boundary + external services — V98-2-3 / ADR-0048 §4 (migration 234);
 * PRD G-5 (boundary per program and per period, external-services register,
 * coverage by period); research §5.1 principle 4; findings round 2 H18 and
 * round 3 ("one derived rule across every provider": Pinecone, Datadog and
 * Anthropic get the same flag and actions).
 *
 *   - Boundary membership is per program (profile) and per period. A period
 *     without rows of its own uses the program's standing boundary
 *     (period_id NULL). Rows are ended, never deleted.
 *   - System facts live on `ai_systems`: handles_federal_data,
 *     internet_reachable, providers_json (provider keys of every
 *     external service the system sends data to).
 *   - A provider's "used by" and "handles federal data" are DERIVED from the
 *     systems, never stored on the provider.
 *   - Coverage per period comes ONLY from coverage documents
 *     (covers_from / covers_to) of the kinds the program accepts — never
 *     green without a document. v9.6 `subservice_orgs` attestations are shown
 *     beside it as `legacy_attestation`, never as coverage.
 *   - `detectSubserviceCoverageGaps` feeds derived-work trigger (a) from the
 *     same documents and `coverageFor`, so the program screens and the SOC 2
 *     carve-out work item never disagree.
 *   - `detectFederalProviderGaps` feeds derived-work trigger (b): a
 *     federal-data system in a FedRAMP boundary depending on a provider that
 *     is not in the register, or is listed without a FedRAMP authorization
 *     document covering today → one owned work item per provider × gap.
 *
 * Every read and write is scoped by the caller's workspace id.
 */
import { listConnectorInterconnections } from './connector-interconnection-service.js';
import { programsWhere } from '../programs/manifests.js';
import { generateRandomId } from '../utils/audit-crypto.js';
import { ACCESS_CHANNELS } from '../connectors/providers.js';
import { containsSecretOrEmail } from '../utils/secret-filter.js';
// ---- types ----------------------------------------------------------------------------
export const EXTERNAL_SERVICE_KINDS = ['model_api', 'vector_db', 'observability', 'hosting', 'data_processing', 'other'];
export const FEDRAMP_STATUSES = ['authorized', 'in_process', 'ready', 'not_authorized', 'unknown'];
/** Migration 334 / ADR-0066 §5. The first five are the GovRAMP Controls Matrix's Assessment Type list. */
export const GOVRAMP_STATUSES = ['authorized', 'provisionally_authorized', 'ready', 'progressing_snapshot', 'one_time_snapshot', 'not_listed', 'unknown'];
export const IMPACT_LEVELS = ['low', 'moderate', 'high'];
/** The four FedRAMP MAS-CSO-TPR free-text fields on a provider (migration 334). */
export const TPR_FIELDS = ['tpr_usage', 'tpr_justification', 'tpr_mitigations', 'compensating_controls'];
export const COVERAGE_DOCUMENT_KINDS = ['soc2_type2', 'soc2_type1', 'bridge_letter', 'fedramp_authorization', 'iso_27001', 'iso_42001', 'other'];
/** Which documents count as coverage for which program. AI Regulatory has no provider-coverage concept. */
export const COVERAGE_KINDS_BY_PROGRAM = Object.freeze(Object.fromEntries(programsWhere(() => true).map((m) => [m.key, m.coverage.documentKinds])));
/** Active program keys whose manifest matches `pred`, for an SQL `IN (…)`. */
function programKeysWhere(pred) {
    return programsWhere(pred).map((m) => m.key);
}
export class BoundaryError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'BoundaryError';
    }
}
// ---- pure helpers --------------------------------------------------------------------------
const DAY_MS = 86_400_000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const day = (d) => d.toISOString().slice(0, 10);
const addDay = (s, n) => day(new Date(Date.parse(`${s}T00:00:00.000Z`) + n * DAY_MS));
/** Provider keys are lower-case slugs ("Anthropic, PBC" → "anthropic-pbc"). */
export function normalizeProviderKey(v) {
    return v.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 100);
}
export function parseProviders(json) {
    try {
        const v = JSON.parse(json ?? '[]');
        if (!Array.isArray(v))
            return [];
        return [...new Set(v.filter((x) => typeof x === 'string').map(normalizeProviderKey).filter(Boolean))].sort();
    }
    catch {
        return [];
    }
}
const bool = (v) => (v === null || v === undefined ? null : v === 1);
/**
 * Coverage of a window by documents (end days inclusive). Only non-withdrawn
 * documents of the accepted kinds count. No accepted kinds → not_applicable.
 */
export function coverageFor(docs, kinds, window, periodId = null) {
    if (kinds.length === 0)
        return { period_id: periodId, window, status: 'not_applicable', gap_from: null, gaps: [], document_ids: [] };
    const used = docs
        .filter((d) => !d.withdrawn_at && kinds.includes(d.kind) && d.covers_to >= window.start && d.covers_from <= window.end)
        .sort((a, b) => (a.covers_from < b.covers_from ? -1 : a.covers_from > b.covers_from ? 1 : a.document_id < b.document_id ? -1 : 1));
    const gaps = [];
    let cursor = window.start; // first day not yet covered
    for (const d of used) {
        if (d.covers_from > cursor)
            gaps.push({ from: cursor, to: addDay(d.covers_from, -1) });
        if (d.covers_to >= cursor)
            cursor = addDay(d.covers_to, 1);
        if (cursor > window.end)
            break;
    }
    if (cursor <= window.end)
        gaps.push({ from: cursor, to: window.end });
    const status = used.length === 0 ? 'not_received' : gaps.length === 0 ? 'covered' : 'partial';
    return { period_id: periodId, window, status, gap_from: gaps[0]?.from ?? null, gaps, document_ids: used.map((d) => d.document_id) };
}
// ---- system facts ----------------------------------------------------------------------------
async function loadSystem(db, workspaceId, systemId) {
    const r = await db.query(`SELECT s.system_id, s.name, s.handles_federal_data, s.internet_reachable, s.providers_json
       FROM ai_systems s LEFT JOIN customer_profiles p ON p.profile_id = s.profile_id
      WHERE s.system_id = ? AND (s.workspace_id = ? OR (s.workspace_id IS NULL AND p.workspace_id = ?)) LIMIT 1`, [systemId, workspaceId, workspaceId]);
    return r.results[0] ?? null;
}
function toFacts(r) {
    return {
        system_id: r.system_id, name: r.name, handles_federal_data: bool(r.handles_federal_data),
        internet_reachable: bool(r.internet_reachable), providers: parseProviders(r.providers_json),
    };
}
/** Every system in the workspace with its boundary facts. */
export async function listSystemFacts(db, workspaceId) {
    const r = await db.query(`SELECT s.system_id, s.name, s.handles_federal_data, s.internet_reachable, s.providers_json
       FROM ai_systems s LEFT JOIN customer_profiles p ON p.profile_id = s.profile_id
      WHERE s.workspace_id = ? OR (s.workspace_id IS NULL AND p.workspace_id = ?)
      ORDER BY s.system_id ASC`, [workspaceId, workspaceId]);
    return r.results.map(toFacts);
}
export async function setSystemFacts(db, input) {
    const cur = await loadSystem(db, input.workspace_id, input.system_id);
    if (!cur)
        throw new BoundaryError('not_found', 'System not found.');
    const sets = [];
    const params = [];
    const tri = (v) => (v === null ? null : v ? 1 : 0);
    if (input.handles_federal_data !== undefined) {
        sets.push('handles_federal_data = ?');
        params.push(tri(input.handles_federal_data));
    }
    if (input.internet_reachable !== undefined) {
        sets.push('internet_reachable = ?');
        params.push(tri(input.internet_reachable));
    }
    if (input.providers !== undefined) {
        if (input.providers.length > 100)
            throw new BoundaryError('invalid', 'At most 100 providers per system.');
        const keys = [...new Set(input.providers.map(normalizeProviderKey).filter(Boolean))].sort();
        sets.push('providers_json = ?');
        params.push(JSON.stringify(keys));
    }
    if (sets.length) {
        await db.execute(`UPDATE ai_systems SET ${sets.join(', ')}, updated_at = ? WHERE system_id = ?`, [...params, (input.now ?? new Date()).toISOString(), input.system_id]);
    }
    return toFacts((await loadSystem(db, input.workspace_id, input.system_id)));
}
// ---- boundary -------------------------------------------------------------------------------
async function activeProfile(db, workspaceId, profileId) {
    const r = await db.query(`SELECT profile_id, program, system_ids_json FROM certification_profiles WHERE profile_id = ? AND workspace_id = ? AND status = 'active' LIMIT 1`, [profileId, workspaceId]);
    return r.results[0] ?? null;
}
async function periodOf(db, workspaceId, profileId, periodId) {
    const r = await db.query(`SELECT period_id, period_start, period_end, status FROM audit_periods WHERE period_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [periodId, workspaceId, profileId]);
    return r.results[0] ?? null;
}
/** Current (open) membership rows of one boundary. */
async function openRows(db, workspaceId, profileId, periodId) {
    const r = await db.query(`SELECT * FROM boundary_memberships WHERE workspace_id = ? AND profile_id = ? AND ${periodId ? 'period_id = ?' : 'period_id IS NULL'} AND removed_at IS NULL
      ORDER BY system_id ASC`, periodId ? [workspaceId, profileId, periodId] : [workspaceId, profileId]);
    return r.results;
}
/**
 * The boundary of a program for a period: the period's own rows, else the
 * standing boundary, else (v9.6 compatibility) the profile's system scope.
 */
export async function getBoundary(db, workspaceId, profileId, periodId = null) {
    const profile = await activeProfile(db, workspaceId, profileId);
    if (!profile)
        throw new BoundaryError('not_found', 'Program not found.');
    if (periodId && !(await periodOf(db, workspaceId, profileId, periodId)))
        throw new BoundaryError('not_found', 'Period not found.');
    const facts = new Map((await listSystemFacts(db, workspaceId)).map((f) => [f.system_id, f]));
    let source = 'empty';
    let rows = periodId ? await openRows(db, workspaceId, profileId, periodId) : [];
    if (rows.length)
        source = 'period';
    else {
        rows = await openRows(db, workspaceId, profileId, null);
        if (rows.length)
            source = 'standing';
    }
    let systems = rows.filter((m) => facts.has(m.system_id)).map((m) => ({ ...facts.get(m.system_id), membership_id: m.membership_id }));
    if (source === 'empty') {
        let scoped = [];
        try {
            scoped = JSON.parse(profile.system_ids_json).filter((x) => typeof x === 'string');
        }
        catch {
            scoped = [];
        }
        systems = scoped.filter((id) => facts.has(id)).sort().map((id) => ({ ...facts.get(id), membership_id: null }));
        if (systems.length)
            source = 'profile_scope';
    }
    return { profile_id: profileId, program: profile.program, period_id: periodId, source, systems, interconnections: await listConnectorInterconnections(db, workspaceId) };
}
export async function addToBoundary(db, input) {
    if (!(await activeProfile(db, input.workspace_id, input.profile_id)))
        throw new BoundaryError('not_found', 'Program not found.');
    const periodId = input.period_id ?? null;
    if (periodId) {
        const p = await periodOf(db, input.workspace_id, input.profile_id, periodId);
        if (!p)
            throw new BoundaryError('not_found', 'Period not found.');
        if (p.status === 'closed')
            throw new BoundaryError('conflict', 'A closed period\'s boundary is part of its record and cannot change.');
    }
    if (!(await loadSystem(db, input.workspace_id, input.system_id)))
        throw new BoundaryError('not_found', 'System not found.');
    const reason = (input.reason ?? '').trim() || null;
    if (reason && reason.length > 2000)
        throw new BoundaryError('invalid', 'reason must be at most 2000 characters.');
    const id = (input.newId ?? (() => generateRandomId('bnd')))();
    try {
        await db.execute(`INSERT INTO boundary_memberships (membership_id, workspace_id, profile_id, period_id, system_id, reason, added_by, added_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.workspace_id, input.profile_id, periodId, input.system_id, reason, input.actor_user_id, (input.now ?? new Date()).toISOString()]);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err)))
            throw new BoundaryError('conflict', 'The system is already in this boundary.');
        throw err;
    }
    return (await db.query(`SELECT * FROM boundary_memberships WHERE membership_id = ? AND workspace_id = ?`, [id, input.workspace_id])).results[0];
}
export async function removeFromBoundary(db, input) {
    const reason = (input.reason ?? '').trim();
    if (!reason)
        throw new BoundaryError('invalid', 'A reason is required to take a system out of a boundary.');
    if (reason.length > 2000)
        throw new BoundaryError('invalid', 'reason must be at most 2000 characters.');
    const r = await db.query(`SELECT * FROM boundary_memberships WHERE membership_id = ? AND workspace_id = ? AND profile_id = ? LIMIT 1`, [input.membership_id, input.workspace_id, input.profile_id]);
    const m = r.results[0];
    if (!m)
        throw new BoundaryError('not_found', 'Boundary membership not found.');
    if (m.removed_at)
        throw new BoundaryError('conflict', 'Already removed.');
    if (m.period_id) {
        const p = await periodOf(db, input.workspace_id, input.profile_id, m.period_id);
        if (p?.status === 'closed')
            throw new BoundaryError('conflict', 'A closed period\'s boundary is part of its record and cannot change.');
    }
    await db.execute(`UPDATE boundary_memberships SET removed_at = ?, removed_by = ?, removed_reason = ? WHERE membership_id = ? AND workspace_id = ? AND removed_at IS NULL`, [(input.now ?? new Date()).toISOString(), input.actor_user_id, reason, input.membership_id, input.workspace_id]);
    return (await db.query(`SELECT * FROM boundary_memberships WHERE membership_id = ? AND workspace_id = ?`, [input.membership_id, input.workspace_id])).results[0];
}
/**
 * Systems in a program's CURRENT boundary: the standing boundary (else the
 * v9.6 profile scope, as `getBoundary` resolves it) plus the boundary of every
 * open (not closed) period. Closed periods' frozen boundaries don't count.
 */
export async function currentBoundarySystemIds(db, workspaceId, profileId) {
    const periods = await db.query(`SELECT period_id FROM audit_periods WHERE workspace_id = ? AND profile_id = ? AND status <> 'closed' ORDER BY period_start ASC, period_id ASC`, [workspaceId, profileId]);
    const ids = new Set();
    for (const pid of [null, ...periods.results.map((p) => p.period_id)]) {
        for (const s of (await getBoundary(db, workspaceId, profileId, pid)).systems)
            ids.add(s.system_id);
    }
    return [...ids].sort();
}
/** Systems in the program's current boundary that send data to the provider (by key). */
export async function boundarySystemsUsingProvider(db, workspaceId, profileId, providerKey) {
    const key = normalizeProviderKey(providerKey);
    if (!key)
        return [];
    const inBoundary = new Set(await currentBoundarySystemIds(db, workspaceId, profileId));
    return (await listSystemFacts(db, workspaceId)).filter((s) => inBoundary.has(s.system_id) && s.providers.includes(key)).map((s) => s.system_id);
}
/** Profiles (active) whose boundary — any period or standing — currently contains the system. */
export async function programsContainingSystem(db, workspaceId, systemId) {
    const r = await db.query(`SELECT DISTINCT b.profile_id FROM boundary_memberships b
       JOIN certification_profiles c ON c.profile_id = b.profile_id AND c.workspace_id = b.workspace_id AND c.status = 'active'
      WHERE b.workspace_id = ? AND b.system_id = ? AND b.removed_at IS NULL ORDER BY b.profile_id ASC`, [workspaceId, systemId]);
    return r.results.map((x) => x.profile_id);
}
function validDate(v, field) {
    if (!DATE_RE.test(v) || Number.isNaN(Date.parse(`${v}T00:00:00.000Z`)))
        throw new BoundaryError('invalid', `${field} must be a date in YYYY-MM-DD form.`);
    return v;
}
async function loadService(db, workspaceId, id) {
    const r = await db.query(`SELECT * FROM external_services WHERE external_service_id = ? AND workspace_id = ? LIMIT 1`, [id, workspaceId]);
    return r.results[0] ?? null;
}
async function assertSubservice(db, workspaceId, subserviceId) {
    if (!subserviceId)
        return;
    const r = await db.query(`SELECT 1 AS ok FROM subservice_orgs WHERE subservice_id = ? AND workspace_id = ? LIMIT 1`, [subserviceId, workspaceId]);
    if (!r.results.length)
        throw new BoundaryError('not_found', 'Subservice organization not found.');
}
const TPR_MAX = 4000;
/** Validate the migration-334 fields that are present; returns the column → value pairs to write. */
function govrampTprValues(input) {
    const out = [];
    if (input.govramp_status !== undefined) {
        if (!GOVRAMP_STATUSES.includes(input.govramp_status))
            throw new BoundaryError('invalid', 'govramp_status is not a known status.');
        out.push(['govramp_status', input.govramp_status]);
    }
    if (input.govramp_package_id !== undefined) {
        const v = (input.govramp_package_id ?? '').trim() || null;
        if (v && (v.length > 200 || containsSecretOrEmail(v)))
            throw new BoundaryError('invalid', 'govramp_package_id must be a marketplace id (at most 200 characters, no credentials).');
        out.push(['govramp_package_id', v]);
    }
    if (input.govramp_status_checked_at !== undefined) {
        const v = input.govramp_status_checked_at;
        if (v !== null && Number.isNaN(Date.parse(v)))
            throw new BoundaryError('invalid', 'govramp_status_checked_at must be a date.');
        out.push(['govramp_status_checked_at', v]);
    }
    if (input.impact_level !== undefined) {
        if (input.impact_level !== null && !IMPACT_LEVELS.includes(input.impact_level))
            throw new BoundaryError('invalid', 'impact_level must be low, moderate or high.');
        out.push(['impact_level', input.impact_level]);
    }
    for (const f of TPR_FIELDS) {
        if (input[f] === undefined)
            continue;
        const v = (input[f] ?? '').trim() || null;
        if (v && v.length > TPR_MAX)
            throw new BoundaryError('invalid', `${f} must be at most ${TPR_MAX} characters.`);
        // ADR-0066 §9: free text never holds a credential or an e-mail address; a hit is refused.
        if (v && containsSecretOrEmail(v))
            throw new BoundaryError('invalid', `${f} looks like it contains a secret or an e-mail address; describe it instead.`);
        out.push([f, v]);
    }
    return out;
}
export async function createExternalService(db, input) {
    const name = (input.name ?? '').trim();
    if (!name || name.length > 200)
        throw new BoundaryError('invalid', 'name is required (at most 200 characters).');
    const key = normalizeProviderKey(input.provider_key ?? name);
    if (!key)
        throw new BoundaryError('invalid', 'provider_key must contain letters or digits.');
    if (input.access_channel != null && !ACCESS_CHANNELS.includes(input.access_channel))
        throw new BoundaryError('invalid', 'access_channel is not a known channel.');
    await assertSubservice(db, input.workspace_id, input.subservice_id);
    const product = (input.product ?? '').trim().toLowerCase() || null;
    if (product && !/^[a-z0-9][a-z0-9-]{0,63}$/.test(product))
        throw new BoundaryError('invalid', 'product must be a product id.');
    const extra = new Map(govrampTprValues(input));
    const id = (input.newId ?? (() => generateRandomId('xs')))();
    const now = (input.now ?? new Date()).toISOString();
    try {
        await db.execute(`INSERT INTO external_services (external_service_id, workspace_id, provider_key, name, service_kind, fedramp_status, fedramp_package_id, subservice_id, notes, access_channel, created_by, created_at, updated_at,
         govramp_status, govramp_package_id, govramp_status_checked_at, impact_level, tpr_usage, tpr_justification, tpr_mitigations, compensating_controls, status_source, product)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.workspace_id, key, name, input.service_kind ?? 'other', input.fedramp_status ?? 'unknown', input.fedramp_package_id ?? null,
            input.subservice_id ?? null, input.notes ?? null, input.access_channel ?? null, input.actor_user_id, now, now,
            extra.get('govramp_status') ?? 'unknown', extra.get('govramp_package_id') ?? null, extra.get('govramp_status_checked_at') ?? null,
            extra.get('impact_level') ?? null, ...TPR_FIELDS.map((f) => extra.get(f) ?? null),
            // ADR-0075 §5: a status a person types is attested; nothing typed is unknown.
            (input.fedramp_status ?? 'unknown') !== 'unknown' || (extra.get('govramp_status') ?? 'unknown') !== 'unknown' ? 'attested' : 'unknown',
            product]);
    }
    catch (err) {
        if (/unique|duplicate/i.test(String(err?.message ?? err)))
            throw new BoundaryError('conflict', `${key}${input.access_channel ? ` (${input.access_channel})` : ''}${product ? ` / ${product}` : ''} is already in the register.`);
        throw err;
    }
    return (await loadService(db, input.workspace_id, id));
}
export async function updateExternalService(db, input) {
    const cur = await loadService(db, input.workspace_id, input.external_service_id);
    if (!cur)
        throw new BoundaryError('not_found', 'External service not found.');
    if (cur.retired_at)
        throw new BoundaryError('conflict', 'A retired external service cannot change.');
    const sets = [];
    const params = [];
    if (input.name !== undefined) {
        const n = input.name.trim();
        if (!n || n.length > 200)
            throw new BoundaryError('invalid', 'name is required (at most 200 characters).');
        sets.push('name = ?');
        params.push(n);
    }
    if (input.provider_key !== undefined) {
        const k = normalizeProviderKey(input.provider_key);
        if (!k)
            throw new BoundaryError('invalid', 'provider_key must contain letters or digits.');
        sets.push('provider_key = ?');
        params.push(k);
    }
    if (input.service_kind !== undefined) {
        sets.push('service_kind = ?');
        params.push(input.service_kind);
    }
    if (input.fedramp_status !== undefined) {
        sets.push('fedramp_status = ?');
        params.push(input.fedramp_status);
    }
    if (input.fedramp_package_id !== undefined) {
        sets.push('fedramp_package_id = ?');
        params.push(input.fedramp_package_id);
    }
    if (input.subservice_id !== undefined) {
        await assertSubservice(db, input.workspace_id, input.subservice_id);
        sets.push('subservice_id = ?');
        params.push(input.subservice_id);
    }
    if (input.notes !== undefined) {
        sets.push('notes = ?');
        params.push(input.notes);
    }
    for (const [c, v] of govrampTprValues(input)) {
        sets.push(`${c} = ?`);
        params.push(v);
    }
    const at = (input.now ?? new Date()).toISOString();
    // ADR-0075 §5: confirming a link stamps who and when; clearing it drops all link fields.
    if (input.fedramp_offering_id !== undefined) {
        const v = (input.fedramp_offering_id ?? '').trim() || null;
        if (v && (v.length > 40 || !/^[A-Za-z0-9-]+$/.test(v)))
            throw new BoundaryError('invalid', 'fedramp_offering_id must be a FedRAMP Marketplace id.');
        sets.push('fedramp_offering_id = ?', 'fedramp_link_source = ?', 'fedramp_link_confirmed_by = ?', 'fedramp_link_confirmed_at = ?');
        params.push(v, v ? 'confirmed' : null, v ? input.actor_user_id : null, v ? at : null);
    }
    if (input.govramp_product_key !== undefined) {
        const v = (input.govramp_product_key ?? '').trim() || null;
        if (v && !/^[0-9a-f]{16}$/.test(v))
            throw new BoundaryError('invalid', 'govramp_product_key must be a GovRAMP product key.');
        sets.push('govramp_product_key = ?');
        params.push(v);
    }
    if (sets.length) {
        try {
            await db.execute(`UPDATE external_services SET ${sets.join(', ')}, updated_at = ? WHERE external_service_id = ? AND workspace_id = ?`, [...params, at, input.external_service_id, input.workspace_id]);
        }
        catch (err) {
            if (/unique|duplicate/i.test(String(err?.message ?? err)))
                throw new BoundaryError('conflict', 'That provider is already in the register.');
            throw err;
        }
        // Where the shown status comes from, recomputed from the stored row (never the sync's doing).
        await db.execute(`UPDATE external_services SET status_source = CASE
         WHEN fedramp_link_source = 'confirmed' AND fedramp_offering_id IS NOT NULL THEN 'linked'
         WHEN fedramp_status <> 'unknown' OR govramp_status <> 'unknown' THEN 'attested'
         ELSE 'unknown' END
       WHERE external_service_id = ? AND workspace_id = ?`, [input.external_service_id, input.workspace_id]);
    }
    return (await loadService(db, input.workspace_id, input.external_service_id));
}
export async function retireExternalService(db, input) {
    const cur = await loadService(db, input.workspace_id, input.external_service_id);
    if (!cur)
        throw new BoundaryError('not_found', 'External service not found.');
    if (cur.retired_at)
        throw new BoundaryError('conflict', 'Already retired.');
    const now = (input.now ?? new Date()).toISOString();
    await db.execute(`UPDATE external_services SET retired_at = ?, retired_by = ?, updated_at = ? WHERE external_service_id = ? AND workspace_id = ? AND retired_at IS NULL`, [now, input.actor_user_id, now, input.external_service_id, input.workspace_id]);
    return (await loadService(db, input.workspace_id, input.external_service_id));
}
export async function addCoverageDocument(db, input) {
    const svc = await loadService(db, input.workspace_id, input.external_service_id);
    if (!svc)
        throw new BoundaryError('not_found', 'External service not found.');
    if (svc.retired_at)
        throw new BoundaryError('conflict', 'A retired external service takes no new documents.');
    if (!COVERAGE_DOCUMENT_KINDS.includes(input.kind))
        throw new BoundaryError('invalid', 'Unknown document kind.');
    const from = validDate(input.covers_from, 'covers_from');
    const to = validDate(input.covers_to, 'covers_to');
    if (to < from)
        throw new BoundaryError('invalid', 'covers_to must be on or after covers_from.');
    const ref = (input.reference ?? '').trim();
    if (!ref || ref.length > 1000)
        throw new BoundaryError('invalid', 'reference (the document itself: evidence id, storage key or URL) is required.');
    const now = input.now ?? new Date();
    const received = input.received_at ? validDate(input.received_at, 'received_at') : day(now);
    const id = (input.newId ?? (() => generateRandomId('xsd')))();
    await db.execute(`INSERT INTO external_service_documents (document_id, workspace_id, external_service_id, kind, covers_from, covers_to, reference, content_sha256, received_at, recorded_by, created_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`, [id, input.workspace_id, input.external_service_id, input.kind, from, to, ref, input.content_sha256 ?? null, received, input.actor_user_id, now.toISOString()]);
    return (await db.query(`SELECT * FROM external_service_documents WHERE document_id = ? AND workspace_id = ?`, [id, input.workspace_id])).results[0];
}
export async function withdrawCoverageDocument(db, input) {
    const reason = (input.reason ?? '').trim();
    if (!reason || reason.length > 2000)
        throw new BoundaryError('invalid', 'A reason is required to withdraw a document.');
    const r = await db.query(`SELECT * FROM external_service_documents WHERE document_id = ? AND workspace_id = ? LIMIT 1`, [input.document_id, input.workspace_id]);
    const d = r.results[0];
    if (!d)
        throw new BoundaryError('not_found', 'Document not found.');
    if (d.withdrawn_at)
        throw new BoundaryError('conflict', 'Already withdrawn.');
    await db.execute(`UPDATE external_service_documents SET withdrawn_at = ?, withdrawn_by = ?, withdrawn_reason = ? WHERE document_id = ? AND workspace_id = ? AND withdrawn_at IS NULL`, [(input.now ?? new Date()).toISOString(), input.actor_user_id, reason, input.document_id, input.workspace_id]);
    return (await db.query(`SELECT * FROM external_service_documents WHERE document_id = ? AND workspace_id = ?`, [input.document_id, input.workspace_id])).results[0];
}
/**
 * The register with derived "used by" / federal-data flag, documents, and —
 * when `profileId` is given — coverage per period of that program (its
 * periods; the program's standing window = today when it has none).
 */
export async function listExternalServices(db, workspaceId, opts = {}) {
    const now = opts.now ?? new Date();
    const svcs = await db.query(`SELECT * FROM external_services WHERE workspace_id = ? ${opts.includeRetired ? '' : 'AND retired_at IS NULL'} ORDER BY provider_key ASC, external_service_id ASC`, [workspaceId]);
    let list = svcs.results;
    if (opts.ids)
        list = list.filter((s) => opts.ids.includes(s.external_service_id));
    const docs = await db.query(`SELECT * FROM external_service_documents WHERE workspace_id = ? ORDER BY covers_from ASC, document_id ASC`, [workspaceId]);
    const systems = await listSystemFacts(db, workspaceId);
    const subs = await db.query(`SELECT subservice_id, soc_report_available, report_period_end FROM subservice_orgs WHERE workspace_id = ?`, [workspaceId]);
    const subById = new Map(subs.results.map((s) => [s.subservice_id, s]));
    let windows = [];
    let kinds = [];
    if (opts.profileId) {
        const prof = await activeProfile(db, workspaceId, opts.profileId);
        if (!prof)
            throw new BoundaryError('not_found', 'Program not found.');
        kinds = COVERAGE_KINDS_BY_PROGRAM[prof.program] ?? [];
        const periods = await db.query(`SELECT period_id, period_start, period_end FROM audit_periods WHERE workspace_id = ? AND profile_id = ? AND status <> 'closed' ORDER BY period_start ASC`, [workspaceId, opts.profileId]);
        windows = periods.results.length
            ? periods.results.map((p) => ({ period_id: p.period_id, start: p.period_start.slice(0, 10), end: p.period_end.slice(0, 10) }))
            : [{ period_id: null, start: day(now), end: day(now) }];
    }
    return list.map((s) => {
        const used = systems.filter((sys) => sys.providers.includes(s.provider_key));
        const answered = used.filter((u) => u.handles_federal_data !== null);
        const mine = docs.results.filter((d) => d.external_service_id === s.external_service_id);
        const sub = s.subservice_id ? subById.get(s.subservice_id) : undefined;
        return {
            ...s,
            used_by: used.map((u) => ({ system_id: u.system_id, name: u.name, handles_federal_data: u.handles_federal_data })),
            handles_federal_data: used.some((u) => u.handles_federal_data === true) ? true : answered.length ? false : null,
            documents: mine,
            coverage: windows.map((w) => coverageFor(mine, kinds, { start: w.start, end: w.end }, w.period_id)),
            legacy_attestation: sub ? { subservice_id: sub.subservice_id, soc_report_available: sub.soc_report_available === 1, report_period_end: sub.report_period_end } : null,
        };
    });
}
/**
 * One coverage source (ADR-0048 §4): for each active SOC 2 period
 * (planned / fieldwork) and each carve-out subservice org, coverage comes
 * from the coverage documents of the live external-services rows related to
 * it (`subservice_id`), of the kinds SOC 2 accepts, over the period window —
 * exactly what `listExternalServices` shows per period. The v9.6
 * `soc_report_available` / `report_period_end` claim is never consulted
 * (it is only shown as `legacy_attestation`). Inclusive-method orgs are
 * tested within the report's own scope, so only carve-outs can have a gap.
 */
export async function detectSubserviceCoverageGaps(db, workspaceId) {
    // Programs whose manifest claims subservice orgs (carve-out / inclusive: SOC 2's system description).
    const programs = programKeysWhere((m) => m.coverage.subserviceOrgs);
    if (programs.length === 0)
        return [];
    const periods = await db.query(
    // d1-in-list: bounded — program keys from the frozen program manifest (a handful)
    `SELECT a.period_id, a.profile_id, c.program, a.period_start, a.period_end FROM audit_periods a
       JOIN certification_profiles c ON c.profile_id = a.profile_id AND c.workspace_id = a.workspace_id
      WHERE a.workspace_id = ? AND a.status IN ('planned', 'fieldwork') AND c.program IN (${programs.map(() => '?').join(', ')})
      ORDER BY a.period_end ASC, a.period_id ASC`, [workspaceId, ...programs]);
    if (periods.results.length === 0)
        return [];
    const orgs = await db.query(`SELECT subservice_id, name FROM subservice_orgs WHERE workspace_id = ? AND method = 'carve_out' ORDER BY subservice_id ASC`, [workspaceId]);
    if (orgs.results.length === 0)
        return [];
    const svcs = await db.query(`SELECT external_service_id, subservice_id FROM external_services
      WHERE workspace_id = ? AND retired_at IS NULL AND subservice_id IS NOT NULL ORDER BY external_service_id ASC`, [workspaceId]);
    const docs = await db.query(`SELECT * FROM external_service_documents WHERE workspace_id = ? ORDER BY covers_from ASC, document_id ASC`, [workspaceId]);
    const out = [];
    for (const p of periods.results) {
        const kinds = COVERAGE_KINDS_BY_PROGRAM[p.program] ?? [];
        for (const o of orgs.results) {
            const ids = svcs.results.filter((s) => s.subservice_id === o.subservice_id).map((s) => s.external_service_id);
            const mine = docs.results.filter((d) => ids.includes(d.external_service_id));
            const coverage = coverageFor(mine, kinds, { start: p.period_start.slice(0, 10), end: p.period_end.slice(0, 10) }, p.period_id);
            if (coverage.status === 'covered')
                continue;
            out.push({
                profile_id: p.profile_id, period_id: p.period_id, period_end: p.period_end, subservice_id: o.subservice_id,
                name: o.name, external_service_ids: ids, coverage,
            });
        }
    }
    return out;
}
/**
 * For each active FedRAMP CR26 program: systems in its boundary (open
 * periods' rows, else the standing boundary, else the v9.6 profile scope)
 * that handle federal data, and the providers they depend on that are not
 * in the register or have no FedRAMP authorization document covering today.
 */
export async function detectFederalProviderGaps(db, workspaceId, now) {
    // Programs whose manifest requires federal-data systems to name a FedRAMP-covered provider.
    const programs = programKeysWhere((m) => m.coverage.federalDataGaps);
    if (programs.length === 0)
        return [];
    const profiles = await db.query(
    // d1-in-list: bounded — program keys from the frozen program manifest (a handful)
    `SELECT profile_id, program FROM certification_profiles
      WHERE workspace_id = ? AND program IN (${programs.map(() => '?').join(', ')}) AND status = 'active' ORDER BY profile_id ASC`, [workspaceId, ...programs]);
    if (profiles.results.length === 0)
        return [];
    const today = day(now);
    const out = [];
    const register = await listExternalServices(db, workspaceId, { now });
    const byKey = new Map(register.map((s) => [s.provider_key, s]));
    for (const { profile_id, program } of profiles.results) {
        const periods = await db.query(`SELECT period_id FROM audit_periods WHERE workspace_id = ? AND profile_id = ? AND status IN ('planned', 'fieldwork') ORDER BY period_start ASC`, [workspaceId, profile_id]);
        const periodIds = periods.results.map((p) => p.period_id);
        const inBoundary = new Map();
        for (const pid of periodIds.length ? periodIds : [null]) {
            for (const s of (await getBoundary(db, workspaceId, profile_id, pid)).systems)
                inBoundary.set(s.system_id, s);
        }
        const byProvider = new Map();
        for (const s of [...inBoundary.values()].sort((a, b) => (a.system_id < b.system_id ? -1 : 1))) {
            if (s.handles_federal_data !== true)
                continue;
            for (const p of s.providers)
                byProvider.set(p, [...(byProvider.get(p) ?? []), { system_id: s.system_id, name: s.name }]);
        }
        for (const [key, systems] of [...byProvider.entries()].sort(([a], [b]) => (a < b ? -1 : 1))) {
            const svc = byKey.get(key);
            if (!svc) {
                out.push({ profile_id, provider_key: key, gap: 'unlisted', external_service_id: null, name: key, systems, period_ids: periodIds });
                continue;
            }
            const cov = coverageFor(svc.documents, COVERAGE_KINDS_BY_PROGRAM[program] ?? [], { start: today, end: today });
            if (cov.status !== 'covered') {
                out.push({ profile_id, provider_key: key, gap: 'no_fedramp_coverage', external_service_id: svc.external_service_id, name: svc.name, systems, period_ids: periodIds });
            }
        }
    }
    return out;
}
//# sourceMappingURL=boundary-service.js.map